import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { AuthService, GlobalMessageUtil, SiteService } from '@card-software/nexus-utilities';

import { BalanceSnapshotRunProxyService } from '../../proxies/inventory/balance-snapshot-run.proxy.service';
import {
  BalanceSnapshotLatest,
  BalanceSnapshotRun,
  BalanceSnapshotRunStatus,
  BalanceSnapshotRunType,
} from '../../models/balance-snapshot-run/balance-snapshot-run';
import {
  balanceSnapshotComputeFieldLabels,
  BalanceSnapshotComputeFormModel,
} from '../../models/balance-snapshot-run/balance-snapshot-run-form';
import { ControlsOf } from '../../utils/form.types';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';

type TagSeverity = 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger';

/** While a run is in progress the table refreshes on this interval, without the loading overlay. */
const REFRESH_INTERVAL_MS = 5000;

/**
 * Balance snapshot runs of the site selected in the site picker (switching sites reloads the page): the site's latest
 * computed month, every compute/backfill run with its status, and a button to compute one month.
 * A viewer (InventoryBalanceViewer) only sees when runs happened: no compute, no user names, no error details.
 */
@Component({
  selector: 'app-balance-snapshot-runs',
  templateUrl: './balance-snapshot-runs.component.html',
  styleUrl: './balance-snapshot-runs.component.scss',
})
export class BalanceSnapshotRunsComponent implements OnInit {
  private readonly proxyService = inject(BalanceSnapshotRunProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  readonly site = SiteService.getSelectedSite();
  readonly maxMonth = new Date();
  readonly fieldLabels = balanceSnapshotComputeFieldLabels;
  readonly statuses = BalanceSnapshotRunStatus;
  readonly isAdmin = AuthService.hasRole(UserGroup.InventoryBalanceAdmin);
  /** Run table columns; a viewer has no "Requested by". */
  readonly columnCount = this.isAdmin ? 8 : 7;

  readonly form = new FormGroup<ControlsOf<BalanceSnapshotComputeFormModel>>({
    month: new FormControl<Date | null>(null, { validators: [Validators.required] }),
  });

  isLoading = signal(0);
  runs = signal<BalanceSnapshotRun[]>([]);
  latest = signal<BalanceSnapshotLatest | null>(null);

  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);
  sortField = signal<string | null>('StartedAt');
  sortOrder = signal(-1);

  /** Only the first page can show a run that just started; that's the one worth refreshing. */
  hasRunInProgress = computed(() => this.runs().some((run) => run.status === BalanceSnapshotRunStatus.InProgress));

  constructor() {
    this.destroyRef.onDestroy(() => this.clearRefresh());
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loadLatest();
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? -1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search();
  }

  computeMonth(): void {
    if (!this.isAdmin) return;
    const site = this.site;
    const month = this.form.controls.month.value;
    if (this.form.invalid || _.isNil(month)) {
      this.form.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.translate('Form_Validation_MissingFieldsTitle', 'Missing fields'),
        this.translate(this.fieldLabels.month, 'Month'),
      );
      return;
    }
    if (_.isNil(site)) {
      GlobalMessageUtil.error(this.translate('Common_Error', 'Error'), this.translate('BalanceSnapshotRun_NoSite', 'Select a site first.'));
      return;
    }

    const label = this.formatMonth(month.getFullYear(), month.getMonth() + 1);
    this.confirmationService.confirm({
      header: this.translate('BalanceSnapshotRun_Compute_ConfirmTitle', 'Compute month'),
      message: this.translate(
        'BalanceSnapshotRun_Compute_ConfirmMessage',
        `Compute the balance snapshot of ${label} for ${site.name}? It replaces that month's snapshots for this site.`,
        { month: label, site: site.name },
      ),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.startCompute(month),
    });
  }

  monthRange(run: BalanceSnapshotRun): string {
    const from = this.formatMonth(run.fromYear, run.fromMonth);
    const to = this.formatMonth(run.toYear, run.toMonth);
    return from === to ? from : `${from} → ${to}`;
  }

  monthCount(run: BalanceSnapshotRun): number {
    return (run.toYear - run.fromYear) * 12 + (run.toMonth - run.fromMonth) + 1;
  }

  formatMonth(year: number, month: number): string {
    return `${year}-${month.toString().padStart(2, '0')}`;
  }

  durationLabel(run: BalanceSnapshotRun): string {
    if (_.isNil(run.finishedAt)) return '-';
    const seconds = Math.round((new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000);
    return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
  }

  statusLabel(status: BalanceSnapshotRunStatus): string {
    switch (status) {
      case BalanceSnapshotRunStatus.InProgress:
        return this.translate('BalanceSnapshotRun_Status_InProgress', 'In progress');
      case BalanceSnapshotRunStatus.Completed:
        return this.translate('BalanceSnapshotRun_Status_Completed', 'Completed');
      case BalanceSnapshotRunStatus.Failed:
        return this.translate('BalanceSnapshotRun_Status_Failed', 'Failed');
    }
  }

  statusSeverity(status: BalanceSnapshotRunStatus): TagSeverity {
    switch (status) {
      case BalanceSnapshotRunStatus.InProgress:
        return 'info';
      case BalanceSnapshotRunStatus.Completed:
        return 'success';
      case BalanceSnapshotRunStatus.Failed:
        return 'danger';
    }
  }

  typeLabel(type: BalanceSnapshotRunType): string {
    return type === BalanceSnapshotRunType.Backfill
      ? this.translate('BalanceSnapshotRun_Type_Backfill', 'Backfill')
      : this.translate('BalanceSnapshotRun_Type_Compute', 'Compute');
  }

  runStrict(run: unknown): BalanceSnapshotRun {
    return run as BalanceSnapshotRun;
  }

  // The site is the request's X-Site-Id, added by the site interceptor.
  private startCompute(month: Date): void {
    this.startLoading();
    this.proxyService
      .compute({ year: month.getFullYear(), month: month.getMonth() + 1 })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('BalanceSnapshotRun_Compute_StartedTitle', 'Compute started'),
            this.translate('BalanceSnapshotRun_Compute_StartedMessage', 'The run is in progress; this page updates when it ends.'),
          );
          this.form.reset();
          this.currentPage.set(1);
          this.search();
        },
      });
  }

  private loadLatest(): void {
    const siteId = this.site?.id;
    this.startLoading();
    this.proxyService
      .getLatestBySite()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.latest.set(response.data?.find((latest) => latest.siteId === siteId) ?? null),
        error: () => this.latest.set(null),
      });
  }

  /** Loads the current page; a background refresh doesn't show the loading overlay. */
  private search(background = false): void {
    this.clearRefresh();
    if (!background) this.startLoading();

    this.proxyService
      .search({}, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => !background && this.stopLoading()))
      .subscribe({
        next: (response) => {
          const wasInProgress = background && this.hasRunInProgress();
          this.runs.set(response.data?.items ?? []);
          this.totalRecords.set(response.data?.totalCount ?? 0);
          // A run just ended: its month may now be the site's latest.
          if (wasInProgress && !this.hasRunInProgress()) this.loadLatest();
          this.scheduleRefresh();
        },
        error: () => {
          this.runs.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private scheduleRefresh(): void {
    if (!this.hasRunInProgress()) return;
    this.refreshTimer = setTimeout(() => this.search(true), REFRESH_INTERVAL_MS);
  }

  private clearRefresh(): void {
    if (_.isNil(this.refreshTimer)) return;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private translate(key: string, fallback: string, params?: Record<string, string>): string {
    const translation: string = this.translateService.instant(key, params);
    return translation === key ? fallback : translation;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }
}
